import { useNavigate, useParams } from 'react-router-dom';
import { articles, stepInfo } from '../../mocks/articles';
import ArticleHeader from './components/ArticleHeader';
import ArticleBody from './components/ArticleBody';
import RelatedArticles from './components/RelatedArticles';
import { useState, useEffect } from 'react';
import RequestFormModal from '../home/components/RequestFormModal';
import InsuranceBanner from './components/InsuranceBanner';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);

  const article = articles.find((a) => a.id === Number(id));

  // Scroll to top on article change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  // Inject JSON-LD structured data for Article and BreadcrumbList
  useEffect(() => {
    if (!article) return;

    const origin = (import.meta.env.VITE_SITE_URL as string)?.replace(/\/$/, '') || window.location.origin;
    const articleUrl = `${origin}/articles/${article.id}`;

    const articleSchema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      image: [article.image],
      datePublished: article.date,
      dateModified: article.date,
      author: {
        '@type': 'Organization',
        name: '鳥取ではじめての家づくり',
        url: origin,
      },
      publisher: {
        '@type': 'Organization',
        name: '鳥取ではじめての家づくり',
        logo: {
          '@type': 'ImageObject',
          url: 'https://public.readdy.ai/ai/img_res/2c450daa-ab1c-4449-ac53-d1ae0dbc5040.png',
        },
      },
      description: article.excerpt ?? article.title,
      keywords: article.tags.join(', '),
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': articleUrl,
      },
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'ホーム',
          item: origin,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: '家づくりガイド',
          item: `${origin}/#content`,
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: article.category,
          item: articleUrl,
        },
      ],
    };

    const existingScript = document.getElementById('article-schema');
    if (existingScript) existingScript.remove();

    const script = document.createElement('script');
    script.id = 'article-schema';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify([articleSchema, breadcrumbSchema]);
    document.head.appendChild(script);

    // Update document title and meta description dynamically
    const prevTitle = document.title;
    document.title = `${article.title} | 鳥取ではじめての家づくり`;
    const metaDesc = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const prevDesc = metaDesc?.content;
    if (metaDesc && article.excerpt) metaDesc.content = article.excerpt;

    return () => {
      document.getElementById('article-schema')?.remove();
      document.title = prevTitle;
      if (metaDesc && prevDesc) metaDesc.content = prevDesc;
    };
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAFAF8]">
        <div className="text-center">
          <p className="text-6xl font-bold text-[#C8D8C0] mb-4">404</p>
          <p className="text-[#6B6B6B] mb-6">記事が見つかりませんでした</p>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="px-6 py-2.5 rounded-full bg-[#8FA882] text-white text-sm font-semibold hover:bg-[#7A9570] transition-colors cursor-pointer whitespace-nowrap"
          >
            ホームに戻る
          </button>
        </div>
      </div>
    );
  }

  const step = stepInfo[article.step];
  const allSteps = Object.entries(stepInfo);

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      {/* Top Nav */}
      <nav className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm border-b border-[#E8DDD0]" aria-label="記事ナビゲーション">
        <div className="max-w-6xl mx-auto px-4 md:px-6 h-14 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 cursor-pointer" aria-label="ホームに戻る">
            <img
              src="https://public.readdy.ai/ai/img_res/2c450daa-ab1c-4449-ac53-d1ae0dbc5040.png"
              alt="鳥取ではじめての家づくり ロゴ"
              loading="eager"
              fetchPriority="low"
              className="h-7 w-auto object-contain"
            />
            <span className="text-sm font-semibold text-[#2C2C2C] whitespace-nowrap hidden md:block">
              鳥取ではじめての家づくり
            </span>
          </a>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="text-sm text-[#6B6B6B] hover:text-[#8FA882] transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line" aria-hidden="true" />
              <span className="hidden sm:inline">記事一覧へ</span>
            </button>
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="px-4 py-2 rounded-full bg-[#D4A96A] text-white text-xs font-bold hover:bg-[#C09050] transition-colors cursor-pointer whitespace-nowrap"
            >
              資料請求（無料）
            </button>
          </div>
        </div>
      </nav>

      {/* Main Layout */}
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8 md:py-10">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-10">
          {/* Article Main */}
          <main className="flex-1 min-w-0" itemScope itemType="https://schema.org/Article">
            <div className="bg-white rounded-2xl p-6 md:p-10">
              <ArticleHeader article={article} />
              <ArticleBody content={article.content} />
              <InsuranceBanner />
              <RelatedArticles
                currentArticleId={article.id}
                currentStep={article.step}
                allArticles={articles}
              />

              {/* Back to home / CTA */}
              <div className="mt-10 pt-6 border-t border-[#EAE4DC] flex flex-col sm:flex-row justify-between items-center gap-4">
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="inline-flex items-center gap-2 text-sm text-[#6B6B6B] hover:text-[#8FA882] transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line" aria-hidden="true" />
                  ホームに戻る
                </button>
                <button
                  type="button"
                  onClick={() => setModalOpen(true)}
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#8FA882] text-white text-sm font-bold hover:bg-[#7A9570] transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-file-list-3-line" aria-hidden="true" />
                  無料で資料請求
                </button>
              </div>
            </div>
          </main>

          {/* Sidebar */}
          <aside className="w-full lg:w-72 flex-shrink-0 flex flex-col gap-5" aria-label="サイドバー">
            {/* Step Progress */}
            <div className="bg-white rounded-2xl p-5">
              <p className="text-xs font-bold text-[#A0A0A0] uppercase tracking-wide mb-4">
                家づくりのステップ
              </p>
              <nav aria-label="家づくりステップナビ">
                <ul className="flex flex-col gap-2.5">
                  {allSteps.map(([num, s]) => {
                    const isActive = num === article.step;
                    return (
                      <li key={num}>
                        <a
                          href={`/steps/${num}`}
                          className={`flex items-center gap-3 p-2.5 rounded-xl transition-colors no-underline ${
                            isActive ? 'bg-[#E8F0E6]' : 'hover:bg-[#F5F1ED]'
                          }`}
                          aria-current={isActive ? 'step' : undefined}
                        >
                          <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                              isActive
                                ? 'bg-[#8FA882] text-white'
                                : 'bg-[#F5F1ED] text-[#A0A0A0]'
                            }`}
                          >
                            {num}
                          </div>
                          <p
                            className={`text-xs font-semibold leading-tight ${
                              isActive ? 'text-[#5A7A52]' : 'text-[#6B6B6B]'
                            }`}
                          >
                            {s.label}
                          </p>
                          {isActive && (
                            <div className="w-4 h-4 flex items-center justify-center ml-auto flex-shrink-0">
                              <i className="ri-arrow-right-s-line text-[#8FA882]" aria-hidden="true" />
                            </div>
                          )}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </nav>
            </div>

            {/* CTA Card */}
            <div className="rounded-2xl overflow-hidden">
              <div className="relative p-6">
                <div className="absolute inset-0">
                  <img
                    src="https://public.readdy.ai/ai/img_res/d238a768799ccc1f605cd0c2ecb7a4ad.jpg"
                    alt=""
                    loading="lazy"
                    decoding="async"
                    width="600"
                    height="500"
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-[#3D5C38]/85" />
                </div>
                <div className="relative z-10">
                  <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/20 text-white/90 text-[10px] font-semibold mb-3">
                    <i className="ri-gift-line" aria-hidden="true" />
                    完全無料
                  </div>
                  <h3 className="text-white font-bold text-base leading-snug mb-2">
                    まずは無料で<br />資料請求してみる
                  </h3>
                  <p className="text-white/80 text-xs leading-relaxed mb-4">
                    間取り・費用・工務店情報を一括でお届け。しつこい営業は一切なし。
                  </p>
                  <button
                    type="button"
                    onClick={() => setModalOpen(true)}
                    className="w-full py-3 rounded-full bg-[#D4A96A] text-white text-sm font-bold hover:bg-[#C09050] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    資料請求はこちら →
                  </button>
                </div>
              </div>
            </div>

            {/* Step articles list */}
            <div className="bg-white rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <span
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold"
                  style={{ backgroundColor: step?.bgColor, color: step?.color }}
                >
                  <i className={step?.icon} aria-hidden="true" />
                  STEP {article.step}
                </span>
                <p className="text-xs font-bold text-[#2C2C2C]">の記事一覧</p>
              </div>
              <nav aria-label="同ステップの記事一覧">
                <ul className="flex flex-col gap-2.5">
                  {articles
                    .filter((a) => a.step === article.step)
                    .map((a) => (
                      <li key={a.id}>
                        <a
                          href={`/articles/${a.id}`}
                          onClick={(e) => { e.preventDefault(); navigate(`/articles/${a.id}`); }}
                          className={`flex items-start gap-2.5 p-2 rounded-lg transition-colors hover:bg-[#F5F1ED] no-underline ${
                            a.id === article.id ? 'bg-[#F5F1ED]' : ''
                          }`}
                          aria-current={a.id === article.id ? 'page' : undefined}
                        >
                          <div className="w-12 h-10 rounded-lg overflow-hidden flex-shrink-0 mt-0.5 bg-[#EDEAE6]">
                            <img
                              src={a.image}
                              alt={a.title}
                              loading="lazy"
                              decoding="async"
                              width="48"
                              height="40"
                              className="w-full h-full object-cover object-top"
                            />
                          </div>
                          <p
                            className={`text-[11px] leading-snug line-clamp-2 ${
                              a.id === article.id
                                ? 'text-[#8FA882] font-semibold'
                                : 'text-[#6B6B6B]'
                            }`}
                          >
                            {a.title}
                          </p>
                        </a>
                      </li>
                    ))}
                </ul>
              </nav>
            </div>
          </aside>
        </div>
      </div>

      <RequestFormModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
}
